import React, { useState, useRef, useEffect, useCallback } from "react";
import { Sparkle, PaperPlaneTilt, X, ChatCircleDots, Headset, CircleNotch } from "@phosphor-icons/react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import api, { apiErr } from "../lib/api";

export default function SupportChat() {
  const [open, setOpen] = useState(false);
  const [conv, setConv] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [needsHuman, setNeedsHuman] = useState(false);
  const endRef = useRef(null);

  const hasAdmin = msgs.some((m) => m.sender === "admin");

  const start = useCallback(async (payload) => {
    const { data } = await api.post("/support/chat/start", payload || {});
    setConv(data.conversation);
    setMsgs(data.messages || []);
    setNeedsHuman(!!data.conversation?.needs_human);
    return data.conversation;
  }, []);

  const sendText = useCallback(async (text, c) => {
    const cv = c || conv;
    if (!text || !cv) return;
    setBusy(true);
    setMsgs((m) => [...m, { sender: "user", text, id: `local_${Date.now()}` }]);
    try {
      const { data } = await api.post("/support/chat/message", { conversation_id: cv.id, text });
      setMsgs(data.messages);
      setNeedsHuman(!!data.needs_human);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  }, [conv]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, open, busy]);

  // Open + prefill when Services "Inquire" (or anything) dispatches the event
  useEffect(() => {
    const handler = async (e) => {
      setOpen(true);
      const detail = e.detail || {};
      try {
        const c = await start({ subject: detail.subject, context: detail.context });
        if (detail.message) await sendText(detail.message, c);
      } catch (err) { toast.error(apiErr(err)); }
    };
    window.addEventListener("eventra:open-chat", handler);
    return () => window.removeEventListener("eventra:open-chat", handler);
  }, [start, sendText]);

  // Lazy-create conversation when opened
  useEffect(() => { if (open && !conv) start().catch(() => {}); }, [open, conv, start]);

  // Poll for admin replies while open
  useEffect(() => {
    if (!open || !conv) return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get("/support/chat");
        if (data.conversation) { setMsgs(data.messages); setNeedsHuman(!!data.conversation.needs_human); }
      } catch { /* ignore */ }
    }, 4000);
    return () => clearInterval(t);
  }, [open, conv]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    await sendText(text);
  };

  const escalate = async () => {
    try {
      const { data } = await api.post("/support/chat/escalate");
      setMsgs(data.messages); setNeedsHuman(true);
      toast.success("Our team has been notified — a human will join shortly.");
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <>
      <button onClick={() => setOpen((o) => !o)} data-testid="support-chat-toggle"
        className="fixed bottom-5 right-5 z-50 grid place-items-center w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-xl btn-press hover:scale-105 transition-transform">
        {open ? <X size={24} weight="bold" /> : <ChatCircleDots size={26} weight="fill" />}
      </button>

      {open && (
        <div data-testid="support-chat-panel"
          className="fixed bottom-24 right-5 z-50 w-[92vw] max-w-sm h-[70vh] max-h-[560px] flex flex-col rounded-2xl border border-border bg-card shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4">
          <div className="bg-foreground text-background px-4 py-3 flex items-center gap-2">
            <span className="grid place-items-center w-8 h-8 rounded-full bg-primary">
              {hasAdmin ? <Headset weight="fill" size={16} /> : <Sparkle weight="fill" size={16} />}
            </span>
            <div>
              <div className="font-heading font-semibold text-sm">EventVexa Support</div>
              <div className="text-[11px] text-background/60">
                {hasAdmin ? "A team member has joined" : "AI answers instantly · human on request"}
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {msgs.length === 0 && (
              <div className="flex justify-start">
                <div className="max-w-[82%] px-3 py-2 rounded-2xl rounded-bl-sm bg-muted text-sm leading-relaxed">
                  Hi! I'm EventVexa's assistant. Ask me about services, wallet, settlements, tickets or organizing — I'll answer right away, or connect you to a human.
                </div>
              </div>
            )}
            {msgs.map((m, i) => (
              <div key={m.id || i} className={`flex ${m.sender === "user" ? "justify-end" : "justify-start"}`}>
                <div>
                  {m.sender === "admin" && <div className="text-[10px] font-semibold text-primary mb-0.5 flex items-center gap-1"><Headset size={11} /> {m.sender_name || "Support team"}</div>}
                  <div data-testid={`support-msg-${m.sender}`}
                    className={`max-w-[82%] px-3 py-2 rounded-2xl text-sm leading-relaxed ${
                      m.sender === "user" ? "bg-primary text-primary-foreground rounded-br-sm"
                        : m.sender === "admin" ? "bg-primary/10 text-foreground border border-primary/20 rounded-bl-sm"
                          : "bg-muted text-foreground rounded-bl-sm"}`}>
                    {m.text}
                  </div>
                </div>
              </div>
            ))}
            {busy && <div className="flex justify-start"><div className="px-3 py-2 rounded-2xl bg-muted"><CircleNotch size={16} className="animate-spin text-primary" /></div></div>}
            {!hasAdmin && !needsHuman && msgs.length > 0 && (
              <button onClick={escalate} data-testid="support-chat-escalate"
                className="mx-auto flex items-center gap-1.5 text-xs font-semibold text-primary border border-primary/30 rounded-full px-3 py-1.5 hover:bg-primary/5">
                <Headset size={14} /> Talk to a human
              </button>
            )}
            {needsHuman && !hasAdmin && (
              <div className="mx-auto text-[11px] text-muted-foreground bg-muted rounded-full px-3 py-1">Waiting for a team member to join…</div>
            )}
            <div ref={endRef} />
          </div>

          <div className="p-2.5 border-t border-border flex gap-2">
            <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Type your message…" className="h-10" data-testid="support-chat-input" />
            <Button onClick={send} disabled={busy} className="h-10 w-10 p-0 rounded-full shrink-0" data-testid="support-chat-send">
              <PaperPlaneTilt weight="fill" size={16} />
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
